// src/features/streams/StreamUtils.ts

import { MediaStatusType, MediaStateType, Codec } from '@lib2/model/media/MediaTypes';
import { DvrArchiveStrategyType } from '@lib2/model/customer/CustomerTypes';
import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { formatSmartDate, formatTimestamp } from '@/shared/utils/date';
import {
  STREAM_TYPE_LABELS,
  STREAM_CREATE_DEFAULTS,
  STREAM_DEFAULT_OUTPUT,
  STREAM_RESOLUTION_PRESETS,
  STREAM_OUTPUT_DEFAULT_RESOLUTION_INDEXES,
} from '@/lib/constants/stream';
import type { IBackendLiveData } from '@/lib/types/Customer';
import type { IMediaStreamScheduleAllData } from '@lib2/model/stream/MediaStreamScheduleTypes';
import type { StreamOutputItem } from './components/StreamOutputsEditor';
import type {
  StreamViewData,
  StreamGridRow,
  StreamLiveStatus,
  RealtimeStreamData,
  StreamCreateRequest,
  StreamMediaUpdateRequest,
  StreamSettingsUpdateRequest,
  StreamOutputData,
} from '@/lib/types/Stream';
import type { StreamType } from '@/lib/constants/stream';
import type { StreamOverviewFormData } from './schemas/StreamForm.schema';

const STATUS_LABELS: Record<MediaStatusType, string> = {
  [MediaStatusType.Ready]: 'Ready',
  [MediaStatusType.Processing]: 'Processing',
  [MediaStatusType.Ingesting]: 'Ingesting',
  [MediaStatusType.AwaitingIngest]: 'Waiting',
  [MediaStatusType.Error]: 'Error',
  [MediaStatusType.Deleted]: 'Deleted',
};

const STATUS_COLORS: Record<MediaStatusType, string> = {
  [MediaStatusType.Ready]: 'green',
  [MediaStatusType.Processing]: 'yellow',
  [MediaStatusType.Ingesting]: 'blue',
  [MediaStatusType.AwaitingIngest]: 'gray',
  [MediaStatusType.Error]: 'red',
  [MediaStatusType.Deleted]: 'gray',
};

export interface CreateStreamFormData {
  title: string;
  descShort: string;
  recordMode: string;
  dvr: boolean;
  encryptHlsAes: boolean;
  outputs: StreamOutputItem[];
}

export class StreamUtils {
  /**
   * Build mediaId → realtime entry map from a polling response.
   * Duplicate mediaId entries (multiple outputs): prefer isRecording === true.
   */
  static buildLiveStatusMap(entries: RealtimeStreamData[]): Map<number, RealtimeStreamData> {
    const map = new Map<number, RealtimeStreamData>();
    for (const entry of entries) {
      const existing = map.get(entry.mediaId);
      if (!existing || (!existing.isRecording && entry.isRecording)) {
        map.set(entry.mediaId, entry);
      }
    }
    return map;
  }

  /** Build StreamGridRow from StreamViewData + live context. */
  static buildRow(
    stream: StreamViewData,
    liveStatusMap: Map<number, RealtimeStreamData>,
    backendMap: Map<number, IBackendLiveData>,
  ): StreamGridRow {
    const media = stream.media;
    const displayId = bcLong.getStr(media.mediaId);
    const status = media.mediaStatusType;
    const isReady = status === MediaStatusType.Ready;
    const isActive = media.mediaStateType === MediaStateType.Active;
    const streamType = (stream.stream?.core?.streamType ?? 'push') as StreamType;
    const backendId = stream.stream?.core?.backendId ?? 0;
    const backend = backendMap.get(backendId);
    const liveEntry = liveStatusMap.get(media.mediaId);

    let liveStatus: StreamLiveStatus = 'offline';
    if (liveEntry) {
      liveStatus = liveEntry.isRecording ? 'recording' : 'live';
    }

    return {
      mediaId: media.mediaId,
      displayId,
      title: media.title,
      status,
      state: media.mediaStateType,
      streamType,
      published: media.published,
      updated: media.updated,
      locationLabel: backend?.locationLabel ?? 'N/A',
      liveStatus,
      statusLabel: isReady ? (isActive ? 'Active' : 'Inactive') : STATUS_LABELS[status],
      statusColor: isReady ? (isActive ? 'green' : 'gray') : STATUS_COLORS[status],
      streamTypeLabel: STREAM_TYPE_LABELS[streamType] ?? streamType,
      publishedFormatted: media.published ? formatSmartDate(media.published) : null,
      publishedTooltip: media.published ? formatTimestamp(media.published) : null,
      updatedFormatted: formatSmartDate(media.updated),
      updatedTooltip: formatTimestamp(media.updated),
      isActive,
      href: `/streams/${displayId}`,
    };
  }

  /**
   * Default output for a new row at the given 0-based position.
   * Follows STREAM_OUTPUT_DEFAULT_RESOLUTION_INDEXES (1080P, 720P, 432P), but never
   * defaults to a higher resolution than the row above; positions beyond the
   * ladder copy the row above.
   */
  static defaultOutputForPosition(position: number, prevResolutionIndex?: number): StreamOutputItem {
    const ladderIndex = STREAM_OUTPUT_DEFAULT_RESOLUTION_INDEXES[position]
      ?? prevResolutionIndex
      ?? STREAM_OUTPUT_DEFAULT_RESOLUTION_INDEXES[0];
    const resolutionIndex = prevResolutionIndex == null
      ? ladderIndex
      : Math.max(ladderIndex, prevResolutionIndex);
    return {
      resolutionIndex,
      totalKbps: STREAM_RESOLUTION_PRESETS[resolutionIndex].recommendedKbps,
    };
  }

  /**
   * Clamp downstream outputs so no output has a higher resolution than the one
   * above it. Presets are ordered highest→lowest, so a lower index means a
   * higher resolution. Returns the same reference when nothing needed clamping.
   */
  static clampOutputsDownstream(list: StreamOutputItem[]): StreamOutputItem[] {
    let changed = false;
    let prevIndex = 0;
    const result = list.map((output, i) => {
      let next = output;
      if (i > 0 && output.resolutionIndex < prevIndex) {
        changed = true;
        next = {
          resolutionIndex: prevIndex,
          totalKbps: STREAM_RESOLUTION_PRESETS[prevIndex].recommendedKbps,
        };
      }
      prevIndex = next.resolutionIndex;
      return next;
    });
    return changed ? result : list;
  }

  /** Transform form data to StreamCreateRequest with legacy-compatible defaults. */
  static buildCreateRequest(formData: CreateStreamFormData, backendId: number): StreamCreateRequest {
    const outputs: StreamOutputData[] = formData.outputs.map((o) => {
      const preset = STREAM_RESOLUTION_PRESETS[o.resolutionIndex];
      const audioKbps = STREAM_DEFAULT_OUTPUT.audioKbps;
      return {
        mediaStreamOutputId: 0,
        mediaId: 0,
        streamOutputSourceType: STREAM_DEFAULT_OUTPUT.streamOutputSourceType,
        audioKbps,
        audioSamplerateHz: STREAM_DEFAULT_OUTPUT.audioSamplerateHz,
        codec: Codec.H264_AAC,
        videoFps: STREAM_DEFAULT_OUTPUT.videoFps,
        videoKbps: Math.max(0, o.totalKbps - audioKbps),
        videoWidth: preset.width,
        videoHeight: preset.height,
        created: 0,
        updated: 0,
      };
    });

    // Multiple outputs → adaptive bitrate via publisher; single → none
    const adaptiveMode = outputs.length > 1 ? 'publisher' : 'none';

    return {
      backendId,
      descShort: formData.descShort || null,
      mediaType: STREAM_CREATE_DEFAULTS.mediaType,
      relatedLinkUrl: null,
      relatedLinkText: null,
      sourceUrl: null,
      title: formData.title,
      outputs,
      pull: null,
      streamType: STREAM_CREATE_DEFAULTS.streamType,
      recordMode: formData.recordMode,
      adaptiveMode,
      dvr: formData.dvr,
      dvrArchiveStrategyType: DvrArchiveStrategyType.Append,
      encryptHlsAes: formData.encryptHlsAes,
      oosMode: STREAM_CREATE_DEFAULTS.oosMode,
      oosEndMins: STREAM_CREATE_DEFAULTS.oosEndMins,
      tags: [],
    };
  }

  /** Filter backends by provisioning + exact DVR/encryption match.
   *  Backend servers have distinct entries per DVR/encryption capability,
   *  so we must match exactly (not permissively).
   *  Returns USA locations first (alphabetical), then others (alphabetical). */
  static filterCompatibleBackends(
    backends: IBackendLiveData[],
    options: { dvr: boolean; encryptHlsAes: boolean },
  ): IBackendLiveData[] {
    const compatible = backends.filter((b) => {
      if (!b.allowProvisioning) return false;
      // Exact DVR match: dvr=false only matches b.dvr=false, dvr=true matches b.dvr=true + strategy
      if (options.dvr) {
        if (!b.dvr || b.dvrArchiveStrategyType !== DvrArchiveStrategyType.Append) return false;
      } else {
        if (b.dvr) return false;
      }
      // Exact encryption match
      if (options.encryptHlsAes !== b.encryptHlsAes) return false;
      return true;
    });

    return StreamUtils.sortBackendsUsaFirst(compatible);
  }

  /** Sort backends: USA locations first (alphabetical), then others (alphabetical).
   *  USA detection matches legacy BackendUtil: locationLabel starts with "us" (case-insensitive). */
  static sortBackendsUsaFirst(backends: IBackendLiveData[]): IBackendLiveData[] {
    const usa: IBackendLiveData[] = [];
    const other: IBackendLiveData[] = [];
    for (const b of backends) {
      if (b.locationLabel.toLowerCase().startsWith('us')) {
        usa.push(b);
      } else {
        other.push(b);
      }
    }
    const byLabel = (a: IBackendLiveData, b: IBackendLiveData) => a.locationLabel.localeCompare(b.locationLabel);
    usa.sort(byLabel);
    other.sort(byLabel);
    return [...usa, ...other];
  }

  /** Select default backend: prefer USA, must match DVR/encryption requirements. */
  static selectDefaultBackend(
    backends: IBackendLiveData[],
    options: { dvr: boolean; encryptHlsAes: boolean },
  ): IBackendLiveData | null {
    const compatible = StreamUtils.filterCompatibleBackends(backends, options);
    if (compatible.length === 0) return null;

    // Prefer USA location
    const usaBackend = compatible.find(
      (b) => b.locationLabel.toLowerCase().includes('usa') || b.locationLabel.toLowerCase().includes('us '),
    );
    return usaBackend ?? compatible[0];
  }

  // --- Detail form transforms ---

  /**
   * Convert StreamViewData to form data for the Overview tab.
   * Null fields become empty strings for form inputs.
   * Preserves backend enum values as-is (e.g., recordMode='schedule' stays 'schedule').
   */
  static transformToOverviewFormData(stream: StreamViewData): StreamOverviewFormData {
    const media = stream.media;
    const record = stream.stream?.record;
    const settings = stream.stream?.settings;
    const offline = stream.stream?.offline;

    return {
      // Stream controls
      recordMode: (record?.recordMode ?? 'manual') as StreamOverviewFormData['recordMode'],
      recordOverride: record?.recordOverride ?? false,
      recordAppendTimeout: record?.recordAppendTimeout ?? 40,
      hasEmbeddedCaptions: settings?.hasEmbeddedCaptions ?? false,
      offlineDisplayMode: (offline?.offlineDisplayMode ?? 'default_message') as StreamOverviewFormData['offlineDisplayMode'],
      offlineMsgHtml: offline?.offlineMsgHtml ?? '',
      // General info
      title: media.title,
      descShort: media.descShort ?? '',
      descLong: media.descLong ?? '',
      refId: media.refId ?? '',
      published: media.published ?? Math.floor(Date.now() / 1000),
      relatedLinkUrl: media.relatedLinkUrl ?? '',
      relatedLinkText: media.relatedLinkText ?? '',
      mediaStateType: media.mediaStateType as 'active' | 'inactive',
      notes: media.notes ?? '',
      tags: stream.tags ?? [],
    };
  }

  /**
   * Transform form data to media update request.
   * Converts empty strings back to null for API.
   * Streams don't use availability fields.
   */
  static transformOverviewToMediaUpdateRequest(
    data: StreamOverviewFormData,
    refIdChanged: boolean,
  ): StreamMediaUpdateRequest {
    return {
      title: data.title,
      descShort: data.descShort || null,
      descLong: data.descLong || null,
      refId: data.refId || null,
      published: data.published,
      availStart: null,
      availEnd: null,
      relatedLinkUrl: data.relatedLinkUrl || null,
      relatedLinkText: data.relatedLinkText || null,
      mediaStateType: data.mediaStateType,
      notes: data.notes || null,
      tags: data.tags,
      adSupported: false,
      refIdChanged,
    };
  }

  /**
   * Transform form data to stream settings update request.
   * Critical: starts from existing stream state and patches only supported fields.
   * Non-editable fields (schedules, advanced offline IDs) are carried forward unchanged.
   */
  static transformOverviewToStreamUpdateRequest(
    data: StreamOverviewFormData,
    stream: StreamViewData,
  ): StreamSettingsUpdateRequest {
    const record = stream.stream?.record;
    const offline = stream.stream?.offline;
    const settings = stream.stream?.settings;
    const core = stream.stream?.core;

    // When recordMode is 'automatic', force recordOverride to false (legacy behavior)
    const recordOverride = data.recordMode === 'automatic' ? false : data.recordOverride;

    // Preserve offlineMsgHtml from original if not editing custom_message
    const offlineMsgHtml = data.offlineDisplayMode === 'custom_message'
      ? (data.offlineMsgHtml || null)
      : (offline?.offlineMsgHtml ?? null);

    return {
      // Editable fields
      recordMode: data.recordMode,
      recordOverride,
      recordAppendTimeout: data.recordAppendTimeout,
      hasEmbeddedCaptions: data.hasEmbeddedCaptions,
      offlineDisplayMode: data.offlineDisplayMode,
      offlineMsgHtml,

      // Carry forward unchanged: schedule data
      scheduleZoneId: core?.scheduleZoneId ?? null,
      schedules: stream.schedules ?? [],

      // Carry forward unchanged: recording transcode
      recordTranscodeVodId: record?.recordTranscodeVodId ?? null,

      // Carry forward unchanged: offline advanced modes
      offlineListId: offline?.offlineListId ?? null,
      offlineMediaId: offline?.offlineMediaId ?? null,
      offlineScheduleCountdown: offline?.offlineScheduleCountdown ?? false,

      // Carry forward unchanged: settings
      adaptiveMode: settings?.adaptiveMode ?? 'none',
      oosMode: settings?.oosMode ?? 'offline',
      oosEndMins: settings?.oosEndMins ?? null,
    };
  }

  /**
   * Build a stream update that patches ONLY schedules + scheduleZoneId.
   * Every other field carries forward from SERVER state (the loaded stream),
   * not the possibly-dirty Overview form — so instant schedule saves cannot
   * clobber unsaved Overview edits, and vice versa.
   */
  static transformSchedulesToStreamUpdateRequest(
    stream: StreamViewData,
    schedules: IMediaStreamScheduleAllData[],
    scheduleZoneId: string,
  ): StreamSettingsUpdateRequest {
    const record = stream.stream?.record;
    const offline = stream.stream?.offline;
    const settings = stream.stream?.settings;

    return {
      // Patched fields
      scheduleZoneId,
      schedules,

      // Carry forward from server state
      recordMode: record?.recordMode ?? 'manual',
      recordOverride: record?.recordOverride ?? false,
      recordAppendTimeout: record?.recordAppendTimeout ?? 40,
      recordTranscodeVodId: record?.recordTranscodeVodId ?? null,
      hasEmbeddedCaptions: settings?.hasEmbeddedCaptions ?? false,
      offlineDisplayMode: offline?.offlineDisplayMode ?? 'default_message',
      offlineMsgHtml: offline?.offlineMsgHtml ?? null,
      offlineListId: offline?.offlineListId ?? null,
      offlineMediaId: offline?.offlineMediaId ?? null,
      offlineScheduleCountdown: offline?.offlineScheduleCountdown ?? false,
      adaptiveMode: settings?.adaptiveMode ?? 'none',
      oosMode: settings?.oosMode ?? 'offline',
      oosEndMins: settings?.oosEndMins ?? null,
    };
  }
}
